/* eslint-disable react/prop-types */
import { lazy } from "react";
import { Navigate, useLocation } from "react-router-dom";
import Icon from "@mui/material/Icon";

// A retired route whose deep links carry meaning (?owner=51). Plain <Navigate to="..."/>
// drops the query string, which would silently downgrade a "open this person" link into
// "open the list".
function RedirectPreservingQuery({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

// Code-split route components to reduce initial bundle size.
// Each lazy() call creates a separate webpack chunk loaded on demand.

// Public / auth (keep sign-in eager — first thing users need)
import SignIn from "layouts/authentication/sign-in";
const SignUp = lazy(() => import("layouts/authentication/sign-up"));
const ForgotPassword = lazy(() => import("layouts/authentication/forgot-password"));
const Unauthorized = lazy(() => import("layouts/authentication/unauthorized"));
const ChangeCredentials = lazy(() => import("layouts/authentication/change-credentials"));
const EmlakOfisleri = lazy(() => import("layouts/emlak_ofisi/liste"));
const EmlakOfisiProfil = lazy(() => import("layouts/emlak_ofisi/profil"));
const EmlakOfisiBasvuru = lazy(() => import("layouts/emlak_ofisi/basvuru"));
const EmlakOfisiBasvuruDurum = lazy(() => import("layouts/emlak_ofisi/basvuru_durum"));
const EmlakOfisiGirisi = lazy(() => import("layouts/emlak_ofisi/giris"));
const VerifyEmail = lazy(() => import("layouts/authentication/verify-email"));

// Public pages
const IlanDetay = lazy(() => import("layouts/ilan-detay"));
const PrivacyPolicy = lazy(() => import("layouts/privacy-policy"));
const Hakkimizda = lazy(() => import("layouts/hakkimizda"));
const Hizmetlerimiz = lazy(() => import("layouts/hizmetlerimiz"));
const Blog = lazy(() => import("layouts/blog"));
const BlogDetail = lazy(() => import("layouts/blog-detail"));
const SiziArayalim = lazy(() => import("layouts/sizi-arayalim"));
const PublicIlan = lazy(() => import("layouts/ilan/public"));
const TermsOfService = lazy(() => import("layouts/terms-of-service"));
const KVKK = lazy(() => import("layouts/kvkk"));
const CookiePolicy = lazy(() => import("layouts/cookie-policy"));
const UyelikIlan = lazy(() => import("layouts/uyelik/ilan"));
const Favoriler = lazy(() => import("layouts/uyelik/favoriler"));
const UyelikArizalarim = lazy(() => import("layouts/uyelik/arizalarim"));
const TeknikHizmetTalep = lazy(() => import("layouts/uyelik/teknik-hizmet"));

// Dashboard
const UnifiedDashboard = lazy(() => import("layouts/dashboard/UnifiedDashboard"));
const NotificationCenter = lazy(() => import("layouts/notifications"));

// Admin - Daireler
const DaireEkle = lazy(() => import("layouts/daireler/daire_ekle"));
const DaireMenu1 = lazy(() => import("layouts/daireler/dairemenu1"));
const CokluDaireEkle = lazy(() => import("layouts/daireler/coklu_daire_ekle"));

// Admin - Kişiler
const KisiEkle = lazy(() => import("layouts/kisiler/kisi_ekle"));
const KisiTable = lazy(() => import("layouts/kisiler/kisi_listesi"));
const CokluKisiEkle = lazy(() => import("layouts/kisiler/coklu_kisi_ekle"));

// Admin - Kontratlar
const Kontrat = lazy(() => import("layouts/kontratlar/kontrat"));
const KontratYonetim = lazy(() => import("layouts/kontratlar/kontrat_yonetim"));
const CokluKontratEkle = lazy(() => import("layouts/kontratlar/coklu_kontrat_ekle"));

// Admin - Arızalar
const ArizaEkle = lazy(() => import("layouts/arizalar/arizaekle"));
const Arizalar = lazy(() => import("layouts/arizalar/ariza"));

// Admin - Finans
const TransferYukle = lazy(() => import("layouts/finans/TransferYukle"));
const CokluTransferYukle = lazy(() => import("layouts/finans/coklu_transfer_yukle"));
const TopluVeriYukleme = lazy(() => import("layouts/toplu_veri_yukleme"));
// Admin - Finans v28 (single-ledger rebuild)
const FinansOzet = lazy(() => import("layouts/finans/v28/ozet"));
const FinansHizmetBedeli = lazy(() => import("layouts/finans/v28/hizmetbedeli"));
const FinansBorclar = lazy(() => import("layouts/finans/v28/borclar"));
const FinansTahsilatlar = lazy(() => import("layouts/finans/v28/tahsilatlar"));
const FinansKasa = lazy(() => import("layouts/finans/v28/kasa"));
const FinansKiraTakip = lazy(() => import("layouts/finans/v28/kiratakip"));
const FinansKisilereGore = lazy(() => import("layouts/finans/v28/kisileregore"));
const FinansBekleyenIslemler = lazy(() => import("layouts/finans/v28/bekleyenislemler"));

// Admin - İlan
const Ilan = lazy(() => import("layouts/ilan"));

// Emlakçı - EİDS kullanıcı kodu doğrulama (Ticaret Bakanlığı e-Devlet akışı)
const EidsDogrulama = lazy(() => import("layouts/emlakci/eids-dogrulama"));

// Kiracı Layouts
const KiraciArizaEkle = lazy(() => import("layouts/kiraci/arizalar/arizaekle"));
const KiraciArizalar = lazy(() => import("layouts/kiraci/arizalar/ariza"));
const KiraciTransferYukle = lazy(() => import("layouts/kiraci/finans/TransferYukle"));
const KiraciTransferler = lazy(() => import("layouts/kiraci/finans/transferler"));
const KiraciBorcAlacak = lazy(() => import("layouts/kiraci/finans/borc_alacak"));
const KiraciOdemeFormu = lazy(() => import("layouts/kiraci/odeme"));
const KiraciOdemeSonuc = lazy(() => import("layouts/kiraci/odeme/OdemeSonuc"));
const AuthenticatedIlan = lazy(() => import("layouts/ilan/AuthenticatedIlan"));

// Usta Layouts
const UstaIslerim = lazy(() => import("layouts/usta/islerim"));
const UstaDekontYukle = lazy(() => import("layouts/usta/finans/dekont_yukle"));
const UstaBorcAlacak = lazy(() => import("layouts/usta/finans/borc_alacak"));

// Ev Sahibi Layouts
const EvSahibiMulklerim = lazy(() => import("layouts/ev_sahibi/mulklerim"));
const EvSahibiKiracilarim = lazy(() => import("layouts/ev_sahibi/kiracilarim"));
const EvSahibiFinans = lazy(() => import("layouts/ev_sahibi/finans"));
const EvSahibiDekontYukle = lazy(() => import("layouts/ev_sahibi/finans/dekont_yukle"));
const EvSahibiArizalar = lazy(() => import("layouts/ev_sahibi/arizalar"));
const EvSahibiArizaEkle = lazy(() => import("layouts/ev_sahibi/arizalar/arizaekle"));
const UyelikBilgiTalepleri = lazy(() => import("layouts/uyelik-bilgi-talepleri"));

// Messaging / Admin
const Mesajlar = lazy(() => import("layouts/mesajlar"));
const BulkCommunication = lazy(() => import("layouts/admin/bulk_communication"));
const Organizasyon = lazy(() => import("layouts/admin/organizasyon"));
const AuditLogs = lazy(() => import("layouts/admin/audit_logs"));
const KisiBirlestirme = lazy(() => import("layouts/admin/kisi_birlestirme"));
const EmlakOfisiBasvurulari = lazy(() => import("layouts/admin/emlak_ofisi"));
const CommunicationLogs = lazy(() => import("layouts/admin/communication_logs"));
const VeriYedekleme = lazy(() => import("layouts/admin/veri_yedekleme"));

// Emlak ofisinin kendi paneli (`/api/ofis/*`). "emlakci" rolünden ayrı bir
// `ofis` rolüyle giriliyor: ofise bağlı olmayan emlakçıların paneli yok.
const OfisOzet = lazy(() => import("layouts/ofis/ozet"));
const OfisProfil = lazy(() => import("layouts/ofis/profil"));
const OfisDanismanlar = lazy(() => import("layouts/ofis/danismanlar"));
const OfisSahibinden = lazy(() => import("layouts/ofis/sahibinden"));

const routes = [
  // ==================== PUBLIC ROUTES ====================
  {
    name: "Sign In",
    key: "sign-in",
    route: "/authentication/sign-in",
    component: <SignIn />,
    noCollapse: true,
    roles: ["public"],
  },
  {
    name: "Sign Up",
    key: "sign-up",
    route: "/authentication/sign-up",
    component: <SignUp />,
    noCollapse: true,
    roles: ["public"],
  },
  {
    name: "Forgot Password",
    key: "forgot-password",
    route: "/authentication/forgot-password",
    component: <ForgotPassword />,
    noCollapse: true,
    roles: ["public"],
  },
  {
    name: "E-posta Doğrulama",
    key: "verify-email",
    route: "/authentication/verify-email",
    component: <VerifyEmail />,
    noCollapse: true,
    roles: ["public"],
  },
  {
    name: "Unauthorized",
    key: "unauthorized",
    route: "/unauthorized",
    component: <Unauthorized />,
    noCollapse: true,
    roles: ["public"],
  },
  {
    type: "rtl",
    name: "İlan Detayı",
    key: "ilan-detay",
    icon: <Icon fontSize="small">info</Icon>,
    route: "/ilan-detay/:id",
    component: <IlanDetay />,
    noCollapse: true,
    roles: ["public", "admin", "kiraci", "emlakci", "ev_sahibi", "usta", "uyelik"],
  },
  {
    name: "Gizlilik Politikası",
    key: "privacy-policy",
    route: "/privacy-policy",
    component: <PrivacyPolicy />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi"],
    noCollapse: true,
  },
  {
    name: "Çerez Politikası",
    key: "cookie-policy",
    route: "/cookie-policy",
    component: <CookiePolicy />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi"],
    noCollapse: true,
  },
  {
    name: "Hakkımızda",
    key: "hakkimizda",
    route: "/hakkimizda",
    component: <Hakkimizda />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi"],
    noCollapse: true,
  },
  {
    name: "Hizmetlerimiz",
    key: "hizmetlerimiz",
    route: "/hizmetlerimiz",
    component: <Hizmetlerimiz />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi"],
    noCollapse: true,
  },
  {
    name: "Blog",
    key: "blog",
    route: "/blog",
    component: <Blog />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi"],
    noCollapse: true,
  },
  {
    name: "Sizi Arayalım",
    key: "sizi-arayalim",
    route: "/sizi-arayalim",
    component: <SiziArayalim />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi"],
    noCollapse: true,
  },
  {
    name: "Blog Detay",
    key: "blog-detail",
    route: "/blog/:slug",
    component: <BlogDetail />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi"],
    noCollapse: true,
  },
  {
    name: "Tüm İlanlar",
    key: "public-ilanlar",
    route: "/ilanlar",
    component: <PublicIlan />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi"],
    noCollapse: true,
  },
  {
    name: "Kullanım Şartları",
    key: "terms-of-service",
    route: "/terms-of-service",
    component: <TermsOfService />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi"],
    noCollapse: true,
  },
  {
    name: "KVKK",
    key: "kvkk",
    route: "/kvkk",
    component: <KVKK />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi"],
    noCollapse: true,
  },

  // ── Emlak ofisi public sayfalari (sahibinden veri tasima basvurusu, talep 1) ──
  // Roller herkesi kapsiyor: sahibinden incelemesi oturum acikken de public
  // profili gorebilmeli, giris yapmis kullanici panele YONLENDIRILMEMELI.
  {
    name: "Emlak Ofisleri",
    key: "emlak-ofisleri",
    route: "/emlak-ofisleri",
    component: <EmlakOfisleri />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi", "emlakci", "uyelik"],
    noCollapse: true,
  },
  {
    name: "Emlak Ofisi Profili",
    key: "emlak-ofisi-profil",
    route: "/emlak-ofisi/:slug",
    component: <EmlakOfisiProfil />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi", "emlakci", "uyelik"],
    noCollapse: true,
  },
  {
    name: "Emlak Ofisi Basvurusu",
    key: "emlak-ofisi-basvuru",
    route: "/emlak-ofisi-basvuru",
    component: <EmlakOfisiBasvuru />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi", "emlakci", "uyelik"],
    noCollapse: true,
  },
  {
    name: "Basvuru Sorgulama",
    key: "emlak-ofisi-basvuru-durum",
    route: "/emlak-ofisi-basvuru/durum",
    component: <EmlakOfisiBasvuruDurum />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi", "emlakci", "uyelik"],
    noCollapse: true,
  },
  {
    name: "Emlak Ofisi Girisi",
    key: "emlak-ofisi-girisi",
    route: "/emlak-ofisi-girisi",
    component: <EmlakOfisiGirisi />,
    roles: ["public", "admin", "kiraci", "usta", "ev_sahibi", "emlakci", "uyelik"],
    noCollapse: true,
  },

  // ==================== COMMON USER ROUTES ====================
  {
    name: "ChangeCredentials",
    key: "change-credentials-authenticated",
    vekalet: "kendi",
    route: "/settings/change-credentials",
    component: <ChangeCredentials />,
    roles: ["admin", "kiraci", "usta", "ev_sahibi", "emlakci", "uyelik", "vekil"],
    noCollapse: true,
  },
  {
    name: "Bildirim Merkezi",
    key: "notification-center",
    vekalet: "kendi",
    route: "/bildirimler",
    component: <NotificationCenter />,
    roles: ["admin", "kiraci", "usta", "ev_sahibi", "emlakci", "uyelik", "vekil"],
    noCollapse: true,
  },

  // ==================== COMMON DASHBOARD ====================
  {
    type: "collapse",
    name: "Özet",
    key: "dashboard",
    vekalet: "okuma",
    icon: <Icon fontSize="small">dashboard</Icon>,
    route: "/dashboard",
    component: <UnifiedDashboard />,
    roles: ["admin", "kiraci", "usta", "ev_sahibi", "vekil"],
  },

  // ==================== ADMIN ROUTES ====================
  {
    type: "collapse",
    name: "Yönetim",
    key: "admin-yonetim",
    icon: <Icon fontSize="small">admin_panel_settings</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Organizasyon",
        key: "admin-organizasyon",
        icon: <Icon fontSize="small">account_tree</Icon>,
        route: "/organizasyon",
        component: <Organizasyon />,
        root_admin_only: true,
      },
      {
        type: "collapse",
        name: "İşlem Kayıtları",
        key: "audit-logs",
        icon: <Icon fontSize="small">history</Icon>,
        route: "/audit-logs",
        component: <AuditLogs />,
      },
      {
        type: "collapse",
        name: "İletişim Kayıtları",
        key: "communication-logs",
        icon: <Icon fontSize="small">chat</Icon>,
        route: "/communication-logs",
        component: <CommunicationLogs />,
      },
      {
        type: "collapse",
        name: "Toplu Veri Yükleme",
        key: "toplu-veri-yukleme",
        icon: <Icon fontSize="small">cloud_upload</Icon>,
        route: "/toplu-veri-yukleme",
        component: <TopluVeriYukleme />,
      },
      {
        type: "collapse",
        name: "Veri Yedekleme",
        key: "veri-yedekleme",
        icon: <Icon fontSize="small">backup</Icon>,
        route: "/veri-yedekleme",
        component: <VeriYedekleme />,
        root_admin_only: true,
      },
    ],
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Kişiler",
    key: "admin-kisiler",
    icon: <Icon fontSize="small">people</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Kişi Ekle",
        key: "admin-kisi_ekle",
        icon: <Icon fontSize="small">person_add</Icon>,
        route: "/kisi_ekle",
        component: <KisiEkle />,
      },
      {
        type: "collapse",
        name: "Çoklu Kişi Ekle",
        key: "coklu_kisi_ekle",
        icon: <Icon fontSize="small">group_add</Icon>,
        route: "/coklu_kisi_ekle",
        component: <CokluKisiEkle />,
      },
      {
        type: "collapse",
        name: "Tüm Kişiler",
        key: "tum_kisiler",
        icon: <Icon fontSize="small">groups</Icon>,
        route: "/tum_kisiler",
        component: <KisiTable initialViewMode="tum_kisiler" />,
      },
      {
        type: "collapse",
        name: "Ev Sahipleri",
        key: "evsahipleri",
        icon: <Icon fontSize="small">house</Icon>,
        route: "/evsahipleri",
        component: <KisiTable initialViewMode="ev_sahipleri" />,
      },
      {
        type: "collapse",
        name: "Kiracılar",
        key: "kiracilar",
        icon: <Icon fontSize="small">group</Icon>,
        route: "/kiracilar",
        component: <KisiTable initialViewMode="kiracilar" />,
      },
      {
        type: "collapse",
        name: "Ustalar",
        key: "ustalar",
        icon: <Icon fontSize="small">engineering</Icon>,
        route: "/ustalar",
        component: <KisiTable initialViewMode="ustalar" />,
      },
      {
        type: "collapse",
        name: "Üye Olanlar",
        key: "uye_olanlar",
        icon: <Icon fontSize="small">how_to_reg</Icon>,
        route: "/uye_olanlar",
        component: <KisiTable initialViewMode="uye_olanlar" />,
      },
      {
        type: "collapse",
        name: "Diğer Kişiler",
        key: "diger_kisiler",
        icon: <Icon fontSize="small">person_outline</Icon>,
        route: "/diger_kisiler",
        component: <KisiTable initialViewMode="diger_kisiler" />,
      },
      {
        type: "collapse",
        name: "Kişi Birleştirme",
        key: "kisi_birlestirme",
        icon: <Icon fontSize="small">merge_type</Icon>,
        route: "/kisi_birlestirme",
        component: <KisiBirlestirme />,
      },
    ],
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Daireler",
    key: "daireler",
    icon: <Icon fontSize="small">apartment</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Daire Ekle",
        key: "daire_ekle",
        icon: <Icon fontSize="small">add_home</Icon>,
        route: "/daire_ekle",
        component: <DaireEkle />,
      },
      {
        type: "collapse",
        name: "Çoklu Daire Ekle",
        key: "coklu_daire_ekle",
        icon: <Icon fontSize="small">domain_add</Icon>,
        route: "/coklu_daire_ekle",
        component: <CokluDaireEkle />,
      },
      {
        type: "collapse",
        name: "Tüm Daireler",
        key: "dairemenu1",
        icon: <Icon fontSize="small">view_list</Icon>,
        route: "/dairemenu1",
        component: <DaireMenu1 initialViewMode="tum" />,
      },
      {
        type: "collapse",
        name: "Kiracılı Daireler",
        key: "dairemenu2",
        icon: <Icon fontSize="small">people_alt</Icon>,
        route: "/dairemenu2",
        component: <DaireMenu1 initialViewMode="kiracili" />,
      },
      {
        type: "collapse",
        name: "Boş Daireler",
        key: "dairemenu3",
        icon: <Icon fontSize="small">door_front</Icon>,
        route: "/dairemenu3",
        component: <DaireMenu1 initialViewMode="bos" />,
      },
      {
        type: "collapse",
        name: "Tahliye Edilecekler",
        key: "dairemenu4",
        icon: <Icon fontSize="small">exit_to_app</Icon>,
        route: "/dairemenu4",
        component: <DaireMenu1 initialViewMode="tahliye" />,
      },
      {
        type: "collapse",
        name: "Teknik Takip",
        key: "teknik_takip",
        icon: <Icon fontSize="small">build</Icon>,
        route: "/teknik_takip",
        component: <DaireMenu1 initialViewMode="teknik" />,
      },
    ],
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "İşyerleri",
    key: "isyerleri",
    icon: <Icon fontSize="small">storefront</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "İşyeri Ekle",
        key: "isyeri_ekle",
        icon: <Icon fontSize="small">add_business</Icon>,
        route: "/isyeri_ekle",
        component: <DaireEkle />,
      },
      {
        type: "collapse",
        name: "Çoklu İşyeri Ekle",
        key: "coklu_isyeri_ekle",
        icon: <Icon fontSize="small">domain_add</Icon>,
        route: "/coklu_isyeri_ekle",
        component: <CokluDaireEkle />,
      },
      {
        type: "collapse",
        name: "Tüm İşyerleri",
        key: "isyerimenu1",
        icon: <Icon fontSize="small">view_list</Icon>,
        route: "/isyerimenu1",
        component: <DaireMenu1 initialViewMode="tum" />,
      },
      {
        type: "collapse",
        name: "Kiracılı İşyerleri",
        key: "isyerimenu2",
        icon: <Icon fontSize="small">people_alt</Icon>,
        route: "/isyerimenu2",
        component: <DaireMenu1 initialViewMode="kiracili" />,
      },
      {
        type: "collapse",
        name: "Boş İşyerleri",
        key: "isyerimenu3",
        icon: <Icon fontSize="small">door_front</Icon>,
        route: "/isyerimenu3",
        component: <DaireMenu1 initialViewMode="bos" />,
      },
      {
        type: "collapse",
        name: "Tahliye Edilecekler",
        key: "isyerimenu4",
        icon: <Icon fontSize="small">exit_to_app</Icon>,
        route: "/isyerimenu4",
        component: <DaireMenu1 initialViewMode="tahliye" />,
      },
      {
        type: "collapse",
        name: "Teknik Takip",
        key: "isyeri_teknik_takip",
        icon: <Icon fontSize="small">build</Icon>,
        route: "/isyeri_teknik_takip",
        component: <DaireMenu1 initialViewMode="teknik" />,
      },
    ],
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "Kontratlar",
    key: "kontratlar",
    icon: <Icon fontSize="small">description</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Kira Kontratları",
        key: "kontrat",
        icon: <Icon fontSize="small">article</Icon>,
        route: "/kontrat",
        component: <Kontrat />,
      },
      {
        type: "collapse",
        name: "Kontrat Yönetim",
        key: "kontrat_yonetim",
        icon: <Icon fontSize="small">manage_accounts</Icon>,
        route: "/kontrat_yonetim",
        component: <KontratYonetim />,
      },
      {
        type: "collapse",
        name: "Çoklu Kontrat Ekle",
        key: "coklu_kontrat_ekle",
        icon: <Icon fontSize="small">post_add</Icon>,
        route: "/coklu_kontrat_ekle",
        component: <CokluKontratEkle />,
      },
    ],
    roles: ["admin"],
  },

  {
    type: "collapse",
    name: "Finans",
    key: "admin-finans",
    icon: <Icon fontSize="small">paid</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Özet",
        key: "finans-ozet",
        icon: <Icon fontSize="small">dashboard</Icon>,
        route: "/finans/ozet",
        component: <FinansOzet />,
      },
      {
        type: "collapse",
        name: "Kira Takip Cetveli",
        key: "finans-kira-takip",
        icon: <Icon fontSize="small">grid_on</Icon>,
        route: "/finans/kira-takip",
        component: <FinansKiraTakip />,
        root_admin_only: true,
      },
      {
        type: "collapse",
        name: "Kişilere Göre",
        key: "finans-kisilere-gore",
        icon: <Icon fontSize="small">groups</Icon>,
        route: "/finans/kisilere-gore",
        component: <FinansKisilereGore />,
      },
      {
        type: "collapse",
        name: "Borçlar",
        key: "finans-borclar",
        icon: <Icon fontSize="small">request_quote</Icon>,
        route: "/finans/borclar",
        component: <FinansBorclar />,
        root_admin_only: true,
      },
      {
        type: "collapse",
        name: "Tahsilatlar",
        key: "finans-tahsilatlar",
        icon: <Icon fontSize="small">receipt_long</Icon>,
        route: "/finans/tahsilatlar",
        component: <FinansTahsilatlar />,
      },
      {
        type: "collapse",
        name: "Bekleyen İşlemler",
        key: "finans-bekleyen-islemler",
        icon: <Icon fontSize="small">manage_search</Icon>,
        route: "/finans/bekleyen-islemler",
        component: <FinansBekleyenIslemler />,
      },
      {
        type: "collapse",
        name: "Hizmet Bedelleri",
        key: "finans-hizmet-bedeli",
        icon: <Icon fontSize="small">event_repeat</Icon>,
        route: "/finans/hizmet-bedeli",
        component: <FinansHizmetBedeli />,
        root_admin_only: true,
      },
      {
        type: "collapse",
        name: "Kasa",
        key: "finans-kasa",
        icon: <Icon fontSize="small">savings</Icon>,
        route: "/finans/kasa",
        component: <FinansKasa />,
        root_admin_only: true,
      },
      {
        type: "collapse",
        name: "Dekont Yükle",
        key: "admin-transfer-yukle",
        icon: <Icon fontSize="small">upload_file</Icon>,
        route: "/transfer-yukle",
        component: <TransferYukle />,
      },
      /*
      {
        type: "collapse",
        name: "Transferler",
        key: "admin-transferler",
        icon: <Icon fontSize="small">receipt</Icon>,
        route: "/transferler",
        component: <Transferler />,
      },
      */
      {
        type: "collapse",
        name: "Çoklu Transfer Yükle",
        key: "coklu-transfer-yukle",
        icon: <Icon fontSize="small">upload_file</Icon>,
        route: "/coklu-transfer-yukle",
        component: <CokluTransferYukle />,
        root_admin_only: true,
      },
    ],
    roles: ["admin"],
  },
  // Bookmarked retired pages remain safe, but are no longer navigable pages.
  {
    key: "redirect-finans-muhasebe",
    route: "/finans/muhasebe",
    component: <Navigate to="/audit-logs" replace />,
  },
  {
    key: "redirect-finans-tahsilat-girisi",
    route: "/finans/tahsilat-girisi",
    component: <Navigate to="/finans/tahsilatlar" replace />,
  },
  // Malik Cari is retired: owner summary, per-unit portfolio, rent-transfer status and
  // the Ödeme/Mahsup/Aktarım actions all live on the person's Cari Kart now, so one
  // person is managed from one screen. The query string is carried over so existing
  // notification rows holding "/finans/malik-cari?owner=51" still open that person's card.
  {
    key: "redirect-finans-malik-cari",
    route: "/finans/malik-cari",
    component: <RedirectPreservingQuery to="/finans/kisilere-gore" />,
  },
  {
    key: "redirect-dekont-onaylama",
    route: "/dekont-onaylama",
    component: <Navigate to="/finans/bekleyen-islemler" replace />,
  },
  {
    key: "redirect-kisi-basi-gelir-gider",
    route: "/kisi-basi-gelir-gider",
    component: <Navigate to="/finans/kisilere-gore" replace />,
  },
  {
    key: "redirect-donemsel-finansal",
    route: "/donemsel-finansal",
    component: <Navigate to="/finans/ozet" replace />,
  },
  {
    key: "redirect-tahsilat",
    route: "/tahsilat",
    component: <Navigate to="/finans/tahsilatlar" replace />,
  },
  {
    key: "redirect-borclandirma",
    route: "/borclandirma",
    component: <Navigate to="/finans/borclar" replace />,
  },
  {
    type: "collapse",
    name: "Teknik Yönetim",
    key: "admin-teknik-yonetim",
    icon: <Icon fontSize="small">build</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Arıza Ekle",
        key: "admin-arizaekle",
        icon: <Icon fontSize="small">notification_add</Icon>,
        route: "/arizaekle",
        component: <ArizaEkle />,
      },
      {
        type: "collapse",
        name: "Arızalar",
        key: "admin-arizalar-list",
        icon: <Icon fontSize="small">report_problem</Icon>,
        route: "/arizalar",
        component: <Arizalar />,
      },
      {
        type: "collapse",
        name: "Ustalar",
        key: "teknik-ustalar",
        icon: <Icon fontSize="small">engineering</Icon>,
        route: "/teknik/ustalar",
        component: <KisiTable initialViewMode="ustalar" />,
      },
    ],
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "İlanlar",
    key: "admin-ilan",
    icon: <Icon fontSize="small">campaign</Icon>,
    route: "/ilan",
    component: <Ilan />,
    roles: ["admin"],
  },
  {
    type: "collapse",
    // `name` SADECE etiket değil, `mergeRoutes` tekilleştirme anahtarı. Public
    // dizin (`/emlak-ofisleri`) de "Emlak Ofisleri" adını taşıyor ve rollerinde
    // "admin" olduğu için ikisinin `getRoutePriority` değeri de 100 çıkıyordu;
    // eşitlikte ilk giren kazandığı için admin ekranı admin kullanıcıda hiç
    // üretilmiyor, adres `path="*"` üzerinden dashboard'a düşüyordu. Ad ayrı
    // kalmalı.
    name: "Emlak Ofisi Başvuruları",
    key: "admin-emlak-ofisi",
    icon: <Icon fontSize="small">storefront</Icon>,
    route: "/emlak-ofisi-basvurulari",
    component: <EmlakOfisiBasvurulari />,
    roles: ["admin"],
  },
  {
    type: "collapse",
    name: "İletişim",
    key: "admin-iletisim",
    icon: <Icon fontSize="small">forum</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Toplu İletişim",
        key: "bulk-communication",
        icon: <Icon fontSize="small">campaign</Icon>,
        route: "/bulk-communication",
        component: <BulkCommunication />,
      },
      {
        type: "collapse",
        name: "Mesajlar",
        key: "admin-mesajlar",
        icon: <Icon fontSize="small">mail</Icon>,
        route: "/mesajlar",
        component: <Mesajlar />,
      },
      {
        type: "collapse",
        name: "Üyelik/Bilgi Talepleri",
        key: "uyelik-bilgi-talepleri",
        icon: <Icon fontSize="small">person_search</Icon>,
        route: "/uyelik-bilgi-talepleri",
        component: <UyelikBilgiTalepleri />,
      },
    ],
    roles: ["admin"],
  },

  // ==================== EMLAKÇI ROUTES ====================
  {
    type: "collapse",
    name: "Emlakçı Yönetimi",
    key: "emlakci-yonetimi",
    icon: <Icon fontSize="small">real_estate_agent</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Daire Ekle",
        key: "emlakci-daire-ekle",
        icon: <Icon fontSize="small">add_home</Icon>,
        route: "/emlakci/daire_ekle",
        component: <DaireEkle />,
      },
      {
        type: "collapse",
        name: "İlanlarım",
        key: "emlakci-ilan",
        icon: <Icon fontSize="small">campaign</Icon>,
        route: "/emlakci/ilan",
        component: <Ilan />,
      },
      {
        type: "collapse",
        name: "EİDS Doğrulama",
        key: "emlakci-eids-dogrulama",
        icon: <Icon fontSize="small">verified_user</Icon>,
        route: "/emlakci/eids-dogrulama",
        component: <EidsDogrulama />,
      },
    ],
    roles: ["emlakci"],
  },

  // ==================== EMLAK OFİSİ (OFİS KULLANICISI) ====================
  // İlan listesi burada YOK: "İlanlarım" (`/emlakci/ilan`) zaten ofis kapsamında
  // çalışıyor (F4a) — ofis yetkilisi oradan ofisin tüm ilanlarını görüyor.
  // İkinci bir ilan ekranı aynı veriyi iki yerden çeker ve zamanla ayrışırdı.
  {
    type: "collapse",
    name: "Ofisim",
    key: "ofis-panel",
    icon: <Icon fontSize="small">storefront</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Özet",
        key: "ofis-ozet",
        icon: <Icon fontSize="small">dashboard</Icon>,
        route: "/ofis/ozet",
        component: <OfisOzet />,
      },
      {
        type: "collapse",
        name: "Danışmanlar",
        key: "ofis-danismanlar",
        icon: <Icon fontSize="small">groups</Icon>,
        route: "/ofis/danismanlar",
        component: <OfisDanismanlar />,
      },
      {
        // Profil düzenleme yalnız ofis yetkilisinde; danışmana menüde de
        // gösterilmiyor (sunucu ayrıca 403 veriyor).
        type: "collapse",
        name: "Ofis Profili",
        key: "ofis-profil",
        icon: <Icon fontSize="small">badge</Icon>,
        route: "/ofis/profil",
        component: <OfisProfil />,
        roles: ["ofis_yetkili"],
      },
      {
        type: "collapse",
        name: "Sahibinden Eşleşmesi",
        key: "ofis-sahibinden",
        icon: <Icon fontSize="small">sync_alt</Icon>,
        route: "/ofis/sahibinden",
        component: <OfisSahibinden />,
      },
    ],
    roles: ["ofis"],
  },

  // ==================== KİRACI ROUTES ====================
  {
    type: "collapse",
    name: "Arızalar",
    key: "kiraci-arizalar",
    icon: <Icon fontSize="small">notification_important</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Arıza Bildir",
        key: "kiraci-arizaekle",
        icon: <Icon fontSize="small">notification_add</Icon>,
        route: "/kiraci/arizaekle",
        component: <KiraciArizaEkle />,
      },
      {
        type: "collapse",
        name: "Arızalarım",
        key: "kiraci-arizalar-list",
        icon: <Icon fontSize="small">report_problem</Icon>,
        route: "/kiraci/arizalar",
        component: <KiraciArizalar />,
      },
    ],
    roles: ["kiraci"],
  },
  {
    type: "collapse",
    name: "Finans",
    key: "kiraci-finans",
    icon: <Icon fontSize="small">paid</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Dekont Yükle",
        key: "kiraci-transfer-yukle",
        icon: <Icon fontSize="small">upload_file</Icon>,
        route: "/kiraci/transfer-yukle",
        component: <KiraciTransferYukle />,
      },
      {
        type: "collapse",
        name: "Ödemelerim",
        key: "kiraci-transferler",
        icon: <Icon fontSize="small">receipt</Icon>,
        route: "/kiraci/transferler",
        component: <KiraciTransferler />,
      },
      {
        type: "collapse",
        name: "Borç / Alacak",
        key: "kiraci-borc-alacak",
        icon: <Icon fontSize="small">account_balance</Icon>,
        route: "/kiraci/borc-alacak",
        component: <KiraciBorcAlacak />,
      },
    ],
    roles: ["kiraci"],
  },
  {
    type: "collapse",
    name: "İlanlar",
    key: "kiraci-ilan",
    icon: <Icon fontSize="small">home</Icon>,
    route: "/kiraci/ilan",
    component: <AuthenticatedIlan />,
    roles: ["kiraci"],
  },
  {
    name: "Online Ödeme",
    key: "kiraci-odeme",
    route: "/kiraci/odeme",
    component: <KiraciOdemeFormu />,
    roles: ["kiraci"],
    noCollapse: true,
  },
  {
    name: "Ödeme Sonucu",
    key: "kiraci-odeme-sonuc",
    route: "/kiraci/odeme-sonuc",
    component: <KiraciOdemeSonuc />,
    roles: ["kiraci", "public"],
    noCollapse: true,
  },
  // ==================== USTA ROUTES ====================
  {
    type: "collapse",
    name: "Finans",
    key: "usta-finans",
    icon: <Icon fontSize="small">paid</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Dekont Yükle",
        key: "usta-dekont-yukle",
        icon: <Icon fontSize="small">upload_file</Icon>,
        route: "/usta/dekont-yukle",
        component: <UstaDekontYukle />,
      },
      {
        type: "collapse",
        name: "Borç / Alacak",
        key: "usta-borc-alacak",
        icon: <Icon fontSize="small">account_balance</Icon>,
        route: "/usta/borc-alacak",
        component: <UstaBorcAlacak />,
      },
    ],
    roles: ["usta"],
  },
  {
    type: "collapse",
    name: "İşlerim",
    key: "usta-islerim",
    icon: <Icon fontSize="small">build</Icon>,
    route: "/usta/islerim",
    component: <UstaIslerim />,
    roles: ["usta"],
  },
  {
    type: "collapse",
    name: "İlanlar",
    key: "usta-ilan",
    icon: <Icon fontSize="small">campaign</Icon>,
    route: "/usta/ilan",
    component: <AuthenticatedIlan />,
    roles: ["usta"],
  },

  // ==================== EV SAHİBİ ROUTES ====================
  {
    type: "collapse",
    name: "Arızalar",
    key: "ev-sahibi-arizalar",
    icon: <Icon fontSize="small">notification_important</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Arıza Bildir",
        key: "ev-sahibi-ariza-ekle",
        vekalet: "yazma",
        icon: <Icon fontSize="small">add_alert</Icon>,
        route: "/ev-sahibi/ariza-bildir",
        component: <EvSahibiArizaEkle />,
      },
      {
        type: "collapse",
        name: "Arızalarım",
        key: "ev-sahibi-ariza-listesi",
        vekalet: "okuma",
        icon: <Icon fontSize="small">build_circle</Icon>,
        route: "/ev-sahibi/arizalarim",
        component: <EvSahibiArizalar />,
      },
    ],
    roles: ["ev_sahibi"],
  },
  {
    type: "collapse",
    name: "Mülklerim",
    key: "ev-sahibi-mulklerim",
    vekalet: "okuma",
    icon: <Icon fontSize="small">apartment</Icon>,
    route: "/ev-sahibi/mulklerim",
    component: <EvSahibiMulklerim />,
    roles: ["ev_sahibi"],
  },
  {
    type: "collapse",
    name: "Kiracılarım",
    key: "ev-sahibi-kiracilarim",
    vekalet: "okuma",
    icon: <Icon fontSize="small">groups</Icon>,
    route: "/ev-sahibi/kiracilarim",
    component: <EvSahibiKiracilarim />,
    roles: ["ev_sahibi"],
  },
  {
    type: "collapse",
    name: "Finans",
    key: "ev-sahibi-finans",
    icon: <Icon fontSize="small">paid</Icon>,
    collapse: [
      {
        type: "collapse",
        name: "Dekont Yükle",
        key: "ev-sahibi-dekont-yukle",
        vekalet: "yazma",
        icon: <Icon fontSize="small">upload_file</Icon>,
        route: "/ev-sahibi/dekont-yukle",
        component: <EvSahibiDekontYukle />,
      },
      {
        type: "collapse",
        name: "Borç / Alacak",
        key: "ev-sahibi-borc-alacak",
        vekalet: "okuma",
        icon: <Icon fontSize="small">account_balance</Icon>,
        route: "/ev-sahibi/finans",
        component: <EvSahibiFinans />,
      },
    ],
    roles: ["ev_sahibi"],
  },
  {
    type: "collapse",
    name: "İlanlar",
    key: "ev-sahibi-ilan",
    icon: <Icon fontSize="small">campaign</Icon>,
    route: "/ev-sahibi/ilan",
    component: <AuthenticatedIlan />,
    roles: ["ev_sahibi"],
  },

  // ==================== ÜYELİK ROUTES ====================
  {
    type: "collapse",
    name: "Taleplerim",
    key: "uyelik-arizalarim",
    icon: <Icon fontSize="small">build</Icon>,
    route: "/uyelik/arizalarim",
    component: <UyelikArizalarim />,
    roles: ["uyelik"],
  },
  {
    type: "collapse",
    name: "İlanlar",
    key: "uyelik-ilan",
    icon: <Icon fontSize="small">home</Icon>,
    route: "/uyelik/ilan",
    component: <UyelikIlan />,
    roles: ["uyelik"],
  },
  {
    type: "collapse",
    name: "Favorilerim",
    key: "uyelik-favoriler",
    icon: <Icon fontSize="small">favorite</Icon>,
    route: "/uyelik/favoriler",
    component: <Favoriler />,
    roles: ["uyelik"],
  },
  {
    type: "collapse",
    name: "Teknik Talep",
    key: "uyelik-teknik-hizmet",
    icon: <Icon fontSize="small">add_task</Icon>,
    route: "/uyelik/teknik-hizmet",
    component: <TeknikHizmetTalep />,
    roles: ["uyelik"],
  },

  // ==================== COMMON FOR ALL AUTHENTICATED USERS ====================
  {
    type: "collapse",
    name: "Mesajlar",
    key: "mesajlar",
    vekalet: "okuma",
    icon: <Icon fontSize="small">mail</Icon>,
    route: "/mesajlar",
    component: <Mesajlar />,
    roles: ["kiraci", "usta", "ev_sahibi", "emlakci", "uyelik"],
  },
];

// Helper to determine route priority
function getRoutePriority(route) {
  if (!route.roles) return 0;
  if (route.roles.includes("admin")) return 100;
  if (route.roles.includes("emlakci")) return 90;
  if (route.roles.includes("ev_sahibi")) return 80;
  if (route.roles.includes("usta")) return 60;
  if (route.roles.includes("kiraci")) return 40;
  if (route.roles.includes("uyelik")) return 20;
  return 0;
}

// Recursive function to merge routes by name
function mergeRoutes(routes) {
  const mergedMap = new Map();

  routes.forEach((route) => {
    if (!mergedMap.has(route.name)) {
      // First time seeing this route name, add it deep cloning to avoid mutation
      mergedMap.set(route.name, { ...route });
    } else {
      const existingRoute = mergedMap.get(route.name);

      // If the new route has higher priority, use its main properties (key, route, icon, component)
      // but we still need to merge their children (collapse)
      if (getRoutePriority(route) > getRoutePriority(existingRoute)) {
        // Keep the children of the existing entry to merge them later
        const existingChildren = existingRoute.collapse || [];

        // Update to the higher priority route props
        Object.assign(existingRoute, route);

        // Restore/Merge children
        if (existingChildren.length > 0) {
          existingRoute.collapse = [...(route.collapse || []), ...existingChildren];
        }
      } else {
        // Existing is higher priority, just append children if any
        if (route.collapse) {
          existingRoute.collapse = [...(existingRoute.collapse || []), ...route.collapse];
        }
      }
    }
  });

  // Convert map back to array and process children recursively
  return Array.from(mergedMap.values()).map((route) => {
    if (route.collapse && route.collapse.length > 0) {
      route.collapse = mergeRoutes(route.collapse);
    }
    return route;
  });
}

// Malik vekaleti (MALIK_VEKALET_PLAN.md): vekil bağlamında menü, sunucudaki
// allow-list gibi fail-closed. Yalnız `vekalet` işaretli sayfalar açılır:
// "okuma" malik kapsamında okur, "yazma" tam yetki ister, "kendi" kişinin kendi
// hesabıdır (bildirimler, şifre). İşaretsiz sayfa malik verisi göstermez.
function vekilBaglamindaAcik(route, vekalet) {
  if (route.roles && route.roles.includes("public")) return true;
  if (route.collapse && !route.route) return true; // klasör: çocukları karar verir
  if (route.vekalet === "yazma") return vekalet.yetki === "tam";
  return route.vekalet === "okuma" || route.vekalet === "kendi";
}

// Recursive helper to filter routes based on roles and root_admin status
function filterRoutesRecursively(routes, user, allowedRoles) {
  return routes.reduce((acc, route) => {
    // 1. Check Root Admin restriction
    if (route.root_admin_only && !user.is_root_admin) {
      return acc; // Skip this route
    }

    if (user.vekalet && !vekilBaglamindaAcik(route, user.vekalet)) {
      return acc;
    }

    // 2. Check Allowed Roles
    if (route.roles) {
      const hasAllowedRole = route.roles.some((role) => allowedRoles.includes(role));
      if (!hasAllowedRole) {
        return acc; // Skip this route
      }
    }

    // 3. Process children (collapse) recursively
    let newCollapse = route.collapse;
    if (newCollapse) {
      newCollapse = filterRoutesRecursively(newCollapse, user, allowedRoles);

      // If filtering resulted in no visible children, and the parent itself has no component/route (folder only),
      // we should skip the parent too.
      // Exception: If the parent has a component or route, keep it even without children (though unlikely for folders)
      if (newCollapse.length === 0 && !route.component && !route.route) {
        return acc;
      }
    }

    // Add valid route to accumulator (with filtered children if any)
    acc.push({ ...route, collapse: newCollapse });
    return acc;
  }, []);
}

// Kullanıcı rolüne göre route'ları filtrele
export function getFilteredRoutes(user) {
  if (!user) {
    // Kullanıcı giriş yapmamış - sadece public route'ları göster
    return routes.filter((route) => route.roles && route.roles.includes("public"));
  }

  const allowedRoles = ["public"];
  if (user.admin) allowedRoles.push("admin");
  if (user.emlakci) allowedRoles.push("emlakci");
  if (user.kiraci) allowedRoles.push("kiraci");
  if (user.usta) allowedRoles.push("usta");
  if (user.ev_sahibi) allowedRoles.push("ev_sahibi");
  if (user.uyelik) allowedRoles.push("uyelik");
  // Kendi rolü olmasa da vekalet sahibi Özet'e (hesap seçici), bildirimlerine
  // ve hesap ayarlarına ulaşabilmeli.
  if (user.vekaletler && user.vekaletler.length > 0) allowedRoles.push("vekil");
  // Ofis paneli emlakçılığa değil, ofise bağlılığa bakıyor: ofissiz emlakçının
  // paneli yok, ofisten çıkarılmış kullanıcı da (sunucuda `Ofis_Aktif`) bu
  // bayrağı almıyor. Menü sınırı; asıl kapı sunucudaki `require_ofis_*`.
  if (user.ofis_kullanicisi) allowedRoles.push("ofis");
  if (user.ofis_yetkilisi) allowedRoles.push("ofis_yetkili");

  // 1. Filter by role recursively
  const roleFilteredRoutes = filterRoutesRecursively(routes, user, allowedRoles);

  // 2. Merge and deduplicate by name
  return mergeRoutes(roleFilteredRoutes);
}
